import { ReactNode, useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ShieldAlert, AlertCircle } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { UnregisteredDeviceOverlay } from '@/components/device';
import { useTwoFactorAuthStore } from '@/store/twoFactorAuth';
import { useModuleSettings, type ModuleKey } from '@/hooks/useModuleSettings';

type Role = 'superadmin' | 'primary_admin' | 'owner' | 'admin' | 'manager' | 'employee';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: Role;
  requiredPermission?: string | string[];
  requireAll?: boolean; // If true, require all permissions; if false, require any
  requiredModule?: ModuleKey | ModuleKey[]; // If set, module must be enabled for org
}

const ROLE_HIERARCHY: Role[] = ['employee', 'manager', 'admin', 'owner', 'primary_admin', 'superadmin'];

export function ProtectedRoute({ 
  children, 
  requiredRole,
  requiredPermission,
  requireAll = false,
  requiredModule,
}: ProtectedRouteProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, isAuthenticated, profile, user } = useAuth();
  const { hasAnyPermission, hasAllPermissions, isLoading: permissionsLoading, effectiveRole, isImpersonating } = usePermissions();
  const { pendingAuth } = useTwoFactorAuthStore();
  const { isModuleEnabled, subscriptionLoading } = useModuleSettings();
  const [loadingTooLong, setLoadingTooLong] = useState(false);
  
  // Track if we've already redirected to prevent multiple redirects
  const hasRedirected = useRef(false);
  
  // Reset redirect flag when location changes (user navigated to a new page)
  useEffect(() => {
    hasRedirected.current = false;
  }, [location.pathname]);

  // Redirect to 2FA verification if pending (only redirect once)
  useEffect(() => {
    if (pendingAuth && user && !hasRedirected.current) {
      hasRedirected.current = true;
      if (pendingAuth.requires2FASetup) {
        navigate('/setup-2fa-required', { replace: true });
      } else {
        navigate('/verify-2fa', { replace: true });
      }
    }
  }, [pendingAuth, user, navigate]);

  // Consider authenticated if we have a user, even if profile hasn't loaded yet
  const hasUser = !!user;
  
  // Only block on initial auth loading, not on profile/permissions loading
  const isInitialLoading = loading && !hasUser;
  
  // Consolidated loading check - wait for both auth and permissions to settle
  const moduleGateReady = !requiredModule || !subscriptionLoading;
  const isFullyLoaded = !loading && !permissionsLoading && moduleGateReady && (profile || isImpersonating);
  const requiredModules = Array.isArray(requiredModule) ? requiredModule : requiredModule ? [requiredModule] : [];

  // Show a warning if loading takes too long
  useEffect(() => {
    if (isInitialLoading) {
      const timer = setTimeout(() => {
        setLoadingTooLong(true);
      }, 5000);
      return () => clearTimeout(timer);
    } else {
      setLoadingTooLong(false);
    }
  }, [isInitialLoading]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!loading && !isAuthenticated && !hasUser) {
      navigate('/login', { replace: true });
    }
  }, [loading, isAuthenticated, hasUser, navigate]);

  // Consolidated access check - only check after fully loaded to prevent flickering
  useEffect(() => {
    // Don't check until everything is loaded
    if (!isFullyLoaded || !isAuthenticated) return;
    
    // Don't redirect if we're already on dashboard or already redirected
    if (location.pathname === '/dashboard' || hasRedirected.current) return;

    let shouldRedirect = false;

    // Check role-based access
    if (requiredRole) {
      const roleToCheck = effectiveRole || 'employee';
      const userRoleIndex = ROLE_HIERARCHY.indexOf(roleToCheck as Role);
      const requiredRoleIndex = ROLE_HIERARCHY.indexOf(requiredRole);

      if (userRoleIndex < requiredRoleIndex) {
        shouldRedirect = true;
      }
    }

    // Check permission-based access
    if (!shouldRedirect && requiredPermission) {
      const permissions = Array.isArray(requiredPermission) ? requiredPermission : [requiredPermission];
      
      const hasAccess = requireAll 
        ? hasAllPermissions(permissions)
        : hasAnyPermission(permissions);

      if (!hasAccess) {
        shouldRedirect = true;
      }
    }

    // Check module-based access
    if (!shouldRedirect && requiredModules.length > 0) {
      const hasModuleAccess = requireAll
        ? requiredModules.every((module) => isModuleEnabled(module))
        : requiredModules.some((module) => isModuleEnabled(module));

      if (!hasModuleAccess) {
        shouldRedirect = true;
      }
    }

    if (shouldRedirect) {
      hasRedirected.current = true;
      navigate('/dashboard', { replace: true });
    }
  }, [isFullyLoaded, isAuthenticated, requiredRole, requiredPermission, requiredModule, requireAll, effectiveRole, isImpersonating, navigate, hasAnyPermission, hasAllPermissions, isModuleEnabled, location.pathname]);

  // Show loading screen during initial auth check OR while permissions are loading
  // This prevents the flash where children render before permissions are checked
  const showLoading = isInitialLoading || (hasUser && !isFullyLoaded);
  
  if (showLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center animate-pulse shadow-lg shadow-blaze-500/30 overflow-hidden">
              <img src="/blazepos-logo.png" alt="BlazePOS" className="w-12 h-12 object-contain" />
            </div>
          </div>
          <div className="flex flex-col items-center gap-2">
            <span className="text-xl font-display font-bold">BlazePOS</span>
            <div className="flex items-center gap-2 text-muted-foreground">
              <div className="w-1.5 h-1.5 rounded-full bg-blaze-500 animate-pulse" />
              <span className="text-sm">Loading your workspace...</span>
            </div>
          </div>
          {loadingTooLong && (
            <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg max-w-md text-center">
              <div className="flex items-center justify-center gap-2 text-amber-700 dark:text-amber-400 mb-2">
                <AlertCircle className="w-4 h-4" />
                <span className="font-medium text-sm">Taking longer than expected</span>
              </div>
              <p className="text-xs text-amber-600 dark:text-amber-500">
                This may be due to a slow network connection or server issues. 
                Please wait or try refreshing the page.
              </p>
              <button 
                onClick={() => window.location.reload()} 
                className="mt-3 px-4 py-1.5 text-xs font-medium bg-amber-100 dark:bg-amber-900/50 hover:bg-amber-200 dark:hover:bg-amber-800/50 text-amber-700 dark:text-amber-300 rounded-md transition-colors"
              >
                Refresh Page
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!isAuthenticated && !hasUser) {
    return null;
  }

  // Check permissions synchronously for rendering (works with impersonation)
  // Only check after fully loaded to prevent flickering
  if (requiredPermission && isFullyLoaded && (profile || isImpersonating)) {
    const permissions = Array.isArray(requiredPermission) ? requiredPermission : [requiredPermission];
    const hasAccess = requireAll 
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions);

    if (!hasAccess) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-950/30 flex items-center justify-center">
              <ShieldAlert className="w-8 h-8 text-red-500" />
            </div>
            <div>
              <h2 className="text-xl font-semibold">Access Denied</h2>
              <p className="text-muted-foreground mt-1">
                {isImpersonating 
                  ? "The user you're impersonating doesn't have permission to access this page."
                  : "You don't have permission to access this page."}
              </p>
            </div>
          </div>
        </div>
      );
    }
  }

  if (requiredModules.length > 0 && isFullyLoaded && (profile || isImpersonating)) {
    const hasModuleAccess = requireAll
      ? requiredModules.every((module) => isModuleEnabled(module))
      : requiredModules.some((module) => isModuleEnabled(module));

    if (!hasModuleAccess) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-950/30 flex items-center justify-center">
              <ShieldAlert className="w-8 h-8 text-amber-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold">Feature Disabled</h2>
              <p className="text-muted-foreground mt-1">
                This feature is disabled for your organization.
              </p>
            </div>
          </div>
        </div>
      );
    }
  }

  return (
    <>
      {/* Block unregistered devices for cashiers */}
      <UnregisteredDeviceOverlay />
      {children}
    </>
  );
}

// Helper component for conditional rendering based on permissions
interface RequirePermissionProps {
  permission: string | string[];
  requireAll?: boolean;
  children: ReactNode;
  fallback?: ReactNode;
}

export function RequirePermission({ 
  permission, 
  requireAll = false, 
  children, 
  fallback = null 
}: RequirePermissionProps) {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();

  const permissions = Array.isArray(permission) ? permission : [permission];
  const hasAccess = requireAll 
    ? hasAllPermissions(permissions)
    : permissions.length === 1 
      ? hasPermission(permissions[0])
      : hasAnyPermission(permissions);

  if (!hasAccess) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}
